import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";

interface FaqSection {
  title: string;
  items: { q: string; a: string }[];
}

const sections: FaqSection[] = [
  {
    title: "Getting started",
    items: [
      { q: "What is MeetHub?", a: "MeetHub is an online meeting platform that runs entirely in your browser. Start an HD video meeting in one click - no downloads, no plugins, no friction." },
      { q: "Do I need to create an account to join a meeting?", a: "No. Anyone with the meeting link can join from any modern browser. You only need an account to host meetings." },
      { q: "Is MeetHub really free?", a: "Yes. The Free plan gives you 10 meetings per month, up to 100 participants, recording up to 1 hour, and live captions. No credit card needed." },
    ],
  },
  {
    title: "Meetings & features",
    items: [
      { q: "How do I run a smooth online meeting?", a: "Send a single meeting link, enable the waiting room, and turn on mute-on-join. MeetHub does this by default and adapts video quality automatically - even guests on weaker connections stay in the call." },
      { q: "Can I record meetings?", a: "Yes. Recordings save to your account, are auto-transcribed, and can be shared via secure link. Free plan recordings are kept for 2 days; paid plans extend retention." },
      { q: "Are breakout rooms supported?", a: "Yes - split a call into smaller groups in seconds, and pull everyone back when you're ready." },
      { q: "Does MeetHub support webinars?", a: "Webinar mode lets hosts control who can speak, manage Q&A, and broadcast to large audiences. Available on Professional and Business plans." },
    ],
  },
  {
    title: "Security & privacy",
    items: [
      { q: "Is MeetHub secure?", a: "All meeting media is encrypted in transit. Hosts can password-protect rooms, enable waiting rooms, lock meetings, and remove participants at any time." },
      { q: "Where is my data stored?", a: "Recordings and account data are stored in privately-secured cloud storage with regular backups. We never sell your data." },
      { q: "Can I delete my data?", a: "Yes. Delete recordings or your entire account at any time from settings." },
    ],
  },
  {
    title: "Billing",
    items: [
      { q: "How do I upgrade or downgrade?", a: "Go to Pricing -> choose a plan -> checkout. Plan changes take effect immediately and we pro-rate the difference." },
      { q: "Can I cancel anytime?", a: "Yes. There are no contracts. Access continues until the end of your current billing period." },
      { q: "Do you offer team or annual discounts?", a: "Yes - talk to sales for annual billing discounts and seat-based pricing." },
    ],
  },
];

export default function FAQ() {
  const navigate = useNavigate();
  const flat = sections.flatMap((s) => s.items);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: flat.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="marketing-page min-h-screen bg-background">
      <Seo
        title="FAQ - MeetHub answers, in plain English"
        description="Answers to the most common questions about MeetHub: getting started, meetings, features, security, and billing."
        canonicalPath="/faq"
        jsonLd={jsonLd}
      />
      <Navigation />

      <section className="relative overflow-hidden bg-gradient-soft pt-24 pb-12 sm:pt-24 border-b">
        <div className="absolute inset-0 bg-gradient-hero pointer-events-none" aria-hidden />
        <div className="container relative max-w-4xl text-center">
          <Badge variant="outline" className="mb-4">
            <HelpCircle className="h-3 w-3 mr-1" /> FAQ
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
            Clear answers for teams that need clarity fast.
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Everything from onboarding to billing and security, written for operators who want decisions without the fluff.
          </p>
        </div>
      </section>

      <section className="pb-12">
        <div className="container max-w-3xl space-y-6">
          {sections.map((section) => (
            <Card key={section.title}>
              <CardContent className="p-6 sm:p-8">
                <h2 className="text-xl font-semibold mb-4">{section.title}</h2>
                <Accordion type="single" collapsible>
                  {section.items.map((item, idx) => (
                    <AccordionItem key={idx} value={`${section.title}-${idx}`}>
                      <AccordionTrigger className="text-left text-base font-medium">{item.q}</AccordionTrigger>
                      <AccordionContent className="text-muted-foreground leading-relaxed">{item.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          ))}

          <Card className="bg-gradient-soft">
            <CardContent className="p-6 sm:p-8 text-center">
              <h3 className="text-xl font-semibold">Still have questions?</h3>
              <p className="text-muted-foreground mt-2">
                Our team responds within one business day.
              </p>
              <Button className="mt-5 h-11 px-6" onClick={() => navigate("/contact")}>
                Contact support <ArrowRight className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
}


